import { Button, Form, Switch } from '@heroui/react'
import {
  queryKeys,
  type UpdateProfileDto,
  useAssignQpaSeeds,
  useCreateUserInvitation,
  useRevokeQpaSeeds,
  useTenantQuotaSummary,
  useUpdateUserProfile,
} from '@qse/api'
import { type ModalComponentProps } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { Alert, HeroInput, StorageQuotaInput, toast } from '@qse/ui'
import { formatStorageValue } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { useMemo, useState } from 'react'

const BYTES_IN_GB = 1024 ** 3

const gbToBytes = (gb: number): number => Math.floor(gb * BYTES_IN_GB)

type RoleChange = 'promotion' | 'demotion' | null

/** The only two transitions the role select offers; any other pairing is not a role change. */
const getRoleChange = (currentRole: string | undefined, targetRole: string): RoleChange => {
  if (currentRole === 'TENANT_USER' && targetRole === 'TENANT_ADMIN') return 'promotion'
  if (currentRole === 'TENANT_ADMIN' && targetRole === 'TENANT_USER') return 'demotion'

  return null
}

export default function AddUser(props: ModalComponentProps) {
  const { setModelActive, onClose, data } = props
  const queryClient = useQueryClient()
  const tenantId = data?.data?.tenantId

  const { data: tenantQuotaSummary } = useTenantQuotaSummary({
    tenantId,
  })

  // Extract quota information from data (prefer latest query data)
  const quotaData = tenantQuotaSummary ?? data?.data?.quotaTenantSummary
  const storageQuota = quotaData?.storage
  const serviceQuota = quotaData?.service
  const qpaQuota = quotaData?.qpa

  const { mutateAsync: createUserInvitation } = useCreateUserInvitation()
  const { mutateAsync: mutateAsyncUpdateUserProfile } = useUpdateUserProfile()
  const { mutateAsync: mutateAssignQpaSeeds } = useAssignQpaSeeds(tenantId)
  const { mutateAsync: mutateRevokeQpaSeeds } = useRevokeQpaSeeds(tenantId)
  const { user } = useUserStore()
  const [error, setError] = useState('')

  // User-level QPA seeds (matches "QPA Assigned" column in users table)
  const currentUserQpaSeeds = data?.data?.quotaSummary?.qpa?.quota ?? 0
  const [qpaEnabled, setQpaEnabled] = useState<boolean>(
    data?.action === 'edit' ? currentUserQpaSeeds > 0 : false,
  )

  // QPA available: remaining pool seeds
  const qpaAllocationRemaining = qpaQuota?.allocationRemaining ?? 0
  const hasQpaPool = (qpaQuota?.quota ?? 0) > 0 || currentUserQpaSeeds > 0

  const currentUserStorageQuotaBytes = data?.data?.quotaSummary?.storage?.quota ?? 0
  const currentUserApiQuota = data?.data?.quotaSummary?.service?.quota || 0

  const storageAllocationRemainingBytes = storageQuota?.allocationRemaining ?? 0
  const storagePendingDisplay = storageQuota?.pendingBytes
    ? formatStorageValue(storageQuota.pendingBytes)
    : null

  const serviceAllocated = serviceQuota?.allocated ?? 0
  const serviceLimit = serviceQuota?.quota ?? 0
  const serviceAllocationRemaining = Math.max(
    0,
    serviceQuota?.allocationRemaining ?? serviceLimit - serviceAllocated,
  )

  const finalAvailableQuotas = useMemo(() => {
    if (data?.action === 'edit') {
      return {
        storageQuotaBytes: storageAllocationRemainingBytes + currentUserStorageQuotaBytes,
        apiQuota: serviceAllocationRemaining + currentUserApiQuota,
      }
    }

    return {
      storageQuotaBytes: storageAllocationRemainingBytes,
      apiQuota: serviceAllocationRemaining,
    }
  }, [
    currentUserApiQuota,
    currentUserStorageQuotaBytes,
    data?.action,
    serviceAllocationRemaining,
    storageAllocationRemainingBytes,
  ])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState<{
    firstName: string
    lastName: string
    phoneNumber: string
    storageQuota: number
    apiQuota: number
    userRole: string
  } | null>(null)

  const submitUpdate = async (params: {
    firstName: string
    lastName: string
    phoneNumber: string
    storageQuota: number
    apiQuota: number
    userRole: string
    isCurrentUserPrimaryAdmin: boolean
  }) => {
    const {
      firstName,
      lastName,
      phoneNumber,
      storageQuota,
      apiQuota,
      userRole,
      isCurrentUserPrimaryAdmin,
    } = params

    const updatePayload: UpdateProfileDto = {
      firstName,
      lastName,
      phoneNumber: phoneNumber || '',
      storageQuota: currentUserStorageQuotaBytes,
      apiQuota: currentUserApiQuota,
      qpaQuota: qpaEnabled ? 1 : 0,
    }

    if (storageQuota && !Number.isNaN(storageQuota) && storageQuota > 0) {
      updatePayload.storageQuota = gbToBytes(storageQuota)
    }

    if (apiQuota && !Number.isNaN(apiQuota) && apiQuota > 0) {
      updatePayload.apiQuota = apiQuota
    }

    if (isCurrentUserPrimaryAdmin && getRoleChange(data?.data?.userRole, userRole)) {
      updatePayload.userRole = userRole
    }
    await mutateAsyncUpdateUserProfile(
      {
        userId: data?.data?.id,
        data: updatePayload,
      },
      {
        onSuccess: async () => {
          // Handle QPA toggle change
          const wasQpaEnabled = currentUserQpaSeeds > 0

          if (qpaEnabled && !wasQpaEnabled && qpaAllocationRemaining > 0) {
            try {
              await mutateAssignQpaSeeds({ userId: data?.data?.id, seeds: 1 })
            } catch {
              toast.error('User updated but QPA access could not be assigned')
            }
          } else if (!qpaEnabled && wasQpaEnabled && currentUserQpaSeeds > 0) {
            try {
              await mutateRevokeQpaSeeds({
                userId: data?.data?.id,
                seeds: currentUserQpaSeeds,
              })
            } catch {
              toast.error('User updated but QPA access could not be revoked')
            }
          }

          queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
          queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
          toast.success('User updated successfully')
          setPendingSubmitData(null)
          setModelActive(undefined)
          onClose()
          setError('')
        },
        onError: error => {
          setError(error.message)
        },
      },
    )
  }

  const handleConfirmRoleChange = async () => {
    if (!pendingSubmitData) return
    setIsSubmitting(true)
    setError('')
    try {
      await submitUpdate({
        ...pendingSubmitData,
        isCurrentUserPrimaryAdmin: data?.data?.currentUserIsPrimary,
      })
    } catch (err) {
      if (err instanceof Error && !error) {
        setError(err.message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const formDataObj = new FormData(e.currentTarget as HTMLFormElement)

    setIsSubmitting(true)

    try {
      const firstName = formDataObj.get('firstName') as string
      const lastName = formDataObj.get('lastName') as string
      const email = formDataObj.get('email') as string
      const phoneNumber = formDataObj.get('phoneNumber') as string
      // The role select is hidden for some targets (e.g. the primary tenant admin);
      // fall back to the current role so a missing field isn't treated as a demotion.
      const userRole =
        (formDataObj.get('userRole') as string) || data?.data?.userRole || 'TENANT_USER'
      const storageQuota = Number(String(formDataObj.get('storageQuota')).replace(/,/g, ''))
      const apiQuota = Number(String(formDataObj.get('apiQuota')).replace(/,/g, ''))

      const payload = {
        firstName,
        lastName,
        email,
        phoneNumber,
        storageQuota: gbToBytes(storageQuota),
        apiQuota,
        userRole,
        tenantId: tenantId || user?.id || '',
        isQpaEnabled: qpaEnabled,
      }

      if (data.action === 'edit') {
        const isCurrentUserPrimaryAdmin = data?.data?.currentUserIsPrimary

        // Demotions are as consequential as promotions, so both pause for confirmation.
        const needsRoleChangeConfirmation =
          isCurrentUserPrimaryAdmin && getRoleChange(data?.data?.userRole, userRole)

        if (needsRoleChangeConfirmation) {
          setPendingSubmitData({
            firstName,
            lastName,
            phoneNumber,
            storageQuota,
            apiQuota,
            userRole,
          })
          setIsSubmitting(false)

          return
        }

        await submitUpdate({
          firstName,
          lastName,
          phoneNumber,
          storageQuota,
          apiQuota,
          userRole,
          isCurrentUserPrimaryAdmin,
        })
      } else {
        await createUserInvitation(payload, {
          onSuccess: async response => {
            // Assign QPA seeds if toggle is on (invitation returns the created user)
            if (qpaEnabled && qpaAllocationRemaining > 0) {
              const newUserId = (response as any)?.id ?? (response as any)?.userId

              if (newUserId) {
                try {
                  await mutateAssignQpaSeeds({ userId: newUserId, seeds: 1 })
                } catch {
                  // Non-fatal: invitation sent, QPA seed assignment failed
                  toast.error('User invited but QPA access could not be assigned')
                }
              }
            }
            // Invalidate both users list and quota data
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
            queryClient.invalidateQueries({ queryKey: queryKeys.quota.tenantSummary(tenantId) })
            toast.success('User created successfully')
            setModelActive(undefined)
            onClose()
            setError('')
          },
          onError: error => {
            setError(error.message)
          },
        })
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  if (pendingSubmitData) {
    const isUpgrade = pendingSubmitData.userRole === 'TENANT_ADMIN'

    return (
      <div className="flex flex-col gap-5 py-5 items-center text-center">
        <div className="w-16 h-16 rounded-full bg-warning-100 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-warning" />
        </div>
        <h2 className="text-xl font-medium">
          {isUpgrade ? 'Upgrade to Tenant Admin?' : 'Downgrade to Tenant User?'}
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed">
          {isUpgrade ? 'You are about to upgrade' : 'You are about to downgrade'}{' '}
          <strong>
            {pendingSubmitData.firstName} {pendingSubmitData.lastName}
          </strong>{' '}
          to <strong>{isUpgrade ? 'Tenant Admin' : 'Tenant User'}</strong>.
        </p>
        {error && <Alert color="danger" description={error} />}
        <div className="flex justify-end gap-3 w-full">
          <Button
            radius="sm"
            variant="flat"
            onPress={() => {
              setError('')
              setPendingSubmitData(null)
            }}
          >
            Go Back
          </Button>
          <Button
            color="warning"
            isLoading={isSubmitting}
            radius="sm"
            onPress={handleConfirmRoleChange}
          >
            {isUpgrade ? 'Confirm Upgrade' : 'Confirm Downgrade'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5 w-full"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        {error && (
          <Alert
            color="danger"
            // title="Error"
            description={error}
          />
        )}

        <HeroInput
          isRequired
          control="text"
          defaultValue={data?.data?.firstName || ''}
          label="First Name"
          labelPlacement="outside"
          name="firstName"
          placeholder="Enter first name"
          validate={(value: string) => {
            if (!value.trim()) {
              return 'First name is required'
            }
            if (value.length > 50) {
              return 'First name must be less than 50 characters'
            }
          }}
        />

        <HeroInput
          isRequired
          control="text"
          defaultValue={data?.data?.lastName || ''}
          label="Last Name"
          labelPlacement="outside"
          name="lastName"
          placeholder="Enter last name"
          validate={(value: string) => {
            if (!value.trim()) {
              return 'Last name is required'
            }
            if (value.length > 50) {
              return 'Last name must be less than 50 characters'
            }
          }}
        />

        <HeroInput
          isRequired
          control="email"
          defaultValue={data?.data?.email || ''}
          isDisabled={data?.action === 'edit'}
          label="Email Address"
          labelPlacement="outside"
          name="email"
          placeholder="Enter email"
          type="email"
          variant="bordered"
          validate={(value: string) => {
            if (!value.trim()) {
              return 'Email Address is required'
            }
          }}
        />

        <HeroInput
          isRequired
          control="phone"
          defaultValue={data?.data?.phoneNumber || ''}
          label="Phone Number"
          labelPlacement="outside"
          name="phoneNumber"
          placeholder="Enter phone number"
        />

        {(data?.action !== 'edit' ||
          (data?.data?.currentUserIsPrimary &&
            (data?.data?.userRole === 'TENANT_USER' ||
              (data?.data?.userRole === 'TENANT_ADMIN' && !data?.data?.isPrimary)))) && (
          <HeroInput
            control="select"
            defaultValue={data?.data?.userRole || 'TENANT_USER'}
            label="Target Role"
            labelPlacement="outside"
            name="userRole"
            placeholder="Select user role"
            options={[
              { key: 'TENANT_USER', label: 'Tenant User' },
              { key: 'TENANT_ADMIN', label: 'Tenant Admin' },
            ]}
          />
        )}
        <div className="w-full">
          <StorageQuotaInput
            allowEmpty={data?.action === 'edit' && !currentUserStorageQuotaBytes}
            availableBytes={finalAvailableQuotas.storageQuotaBytes}
            defaultValueBytes={currentUserStorageQuotaBytes || undefined}
            name="storageQuota"
          />
          {storagePendingDisplay ? (
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Pending storage: {storagePendingDisplay}
            </div>
          ) : null}
        </div>

        <div className="space-y-2 w-full">
          <HeroInput
            isRequired
            control="number"
            defaultValue={currentUserApiQuota}
            label="API Quota"
            labelPlacement="outside"
            minValue={1}
            name="apiQuota"
            placeholder="Enter API quota"
            validate={(value: number) => {
              // In edit mode, allow empty value if user doesn't have quota set yet
              if (data?.action === 'edit' && !value && !currentUserApiQuota) {
                return undefined
              }
              if (!value) {
                return 'API Quota is required'
              }
              if (value <= 0) {
                return 'API quota must be greater than 0'
              }
              if (value > finalAvailableQuotas.apiQuota) {
                if (data?.action === 'edit') {
                  return `API quota too high. Requested: ${value.toLocaleString()}, Max allowed: ${finalAvailableQuotas.apiQuota.toLocaleString()} (remaining: ${serviceAllocationRemaining.toLocaleString()} + current: ${currentUserApiQuota.toLocaleString()})`
                } else {
                  return `Insufficient API quota. Requested: ${value.toLocaleString()}, Remaining: ${serviceAllocationRemaining.toLocaleString()} (allocated: ${serviceAllocated.toLocaleString()} / limit: ${serviceLimit.toLocaleString()})`
                }
              }
            }}
          />

          {/* Display available API quota */}
          <div className="text-xs text-gray-400">
            {data?.action === 'edit' ? (
              <>
                Max API quota: {finalAvailableQuotas.apiQuota.toLocaleString()} (remaining:{' '}
                {serviceAllocationRemaining.toLocaleString()} + current:{' '}
                {currentUserApiQuota.toLocaleString()})
              </>
            ) : (
              <>Available API quota: {serviceAllocationRemaining.toLocaleString()} (remaining)</>
            )}
          </div>
        </div>

        {hasQpaPool && (
          <div className="w-full rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">QPA Access</p>
                <p className="text-xs text-gray-500 dark:text-[#AFAFAF]">
                  {qpaEnabled
                    ? 'User has QPA assessment access'
                    : qpaAllocationRemaining > 0
                      ? `${qpaAllocationRemaining} seed${qpaAllocationRemaining !== 1 ? 's' : ''} available in pool`
                      : 'No seeds available in pool'}
                </p>
              </div>
            </div>
            <Switch
              isDisabled={!qpaEnabled && qpaAllocationRemaining <= 0}
              isSelected={qpaEnabled}
              size="sm"
              onValueChange={setQpaEnabled}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 w-full">
          <Button disabled={isSubmitting} radius="sm" variant="flat" onPress={handleCancel}>
            Cancel
          </Button>
          <Button color="primary" isLoading={isSubmitting} radius="sm" type="submit">
            {data?.action === 'edit' ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
